import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

export interface AllocationRow {
  id: string;
  portfolio_id: string;
  asset_id: string;
  asset_type: string;
  allocation_percentage: number | null;
  allocation_value: number | null;
  is_value_locked: boolean | null;
  currency_code: string | null;
}

export interface FundData {
  id: string;
  asset_class?: string;
  sa_asset_class?: string;
  fund_name?: string;
  investment_strategy?: string;
  source?: 'funds' | 'funds_raw';
}

export async function fetchEnhancedPortfolios() {
  // Step 1: Fetch all portfolios
  const { data: portfoliosData, error: portfoliosError } = await supabase
    .from('portfolios')
    .select('*')
    .order('created_at', { ascending: false });

  if (portfoliosError) throw portfoliosError;
  if (!portfoliosData || (portfoliosData?.length || 0) === 0) {
    logger.debug('PortfolioQueryService', 'No portfolios found');
    return [] as any[];
  }

  const portfolioIds = (portfoliosData || []).map((p: any) => p.id);

  // Step 2: Fetch all allocations for these portfolios
  const { data: allocationsData, error: allocationsError } = await supabase
    .from('portfolio_allocations')
    .select('*, asset_id, asset_type, allocation_percentage, allocation_value, is_value_locked, currency_code')
    .in('portfolio_id', portfolioIds);

  if (allocationsError) {
    logger.error('PortfolioQueryService', 'Error fetching allocations', { allocationsError });
  }
  const typedAllocations: AllocationRow[] = (allocationsData as AllocationRow[]) || [];

  // Step 3: Fetch fund metadata
  const fundIds = typedAllocations.map(a => a.asset_id).filter(Boolean);

  const [{ data: fundsData, error: fundsError }, { data: fundsRawData, error: fundsRawError }] = await Promise.all([
    supabase.from('funds').select('id, investment_strategy, asset_class').in('id', fundIds),
    supabase.from('funds_raw').select('id, fund_name, sa_asset_class').in('id', fundIds),
  ]);

  if (fundsError) logger.error('PortfolioQueryService', 'Error fetching funds data', { fundsError });
  if (fundsRawError) logger.error('PortfolioQueryService', 'Error fetching funds_raw data', { fundsRawError });

  const fundMap: Record<string, FundData> = {};
  if (fundsData) {
    for (const fund of fundsData as any[]) {
      fundMap[fund.id] = { ...fund, source: 'funds' } as FundData;
    }
  }
  if (fundsRawData) {
    for (const fund of fundsRawData as any[]) {
      fundMap[fund.id] = { ...fund, asset_class: (fund as any)?.sa_asset_class, source: 'funds_raw' } as FundData;
    }
  }

  // Step 4: Attach allocations and fund data to portfolios
  const enhanced = (portfoliosData || []).map((portfolio: any) => {
    const portfolioAllocations = typedAllocations
      .filter(a => a.portfolio_id === portfolio.id)
      .map(a => {
        const fd = fundMap[a.asset_id] || {
          id: a.asset_id,
          asset_class: a.asset_type || 'unclassified',
          fund_name: 'Unknown Asset',
        } as FundData;
        return { ...a, fund: fd };
      });
    return { ...portfolio, portfolio_allocations: portfolioAllocations };
  });

  return enhanced;
}
